import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, User, Mail, Phone, Hash, Calendar,
  DollarSign, Target, Trash2, CheckCircle2, MessageSquare,
  TrendingUp, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ACTIVITY_TYPE_LABELS, formatStageChangeDescription } from '../constants/leadConstants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';

const LEAD_SOURCES = [
  'Referido', 'Sitio Web', 'Facebook', 'Instagram', 'WhatsApp',
  'Llamada Telefónica', 'Visita Directa', 'Otro'
];

const PRIORITIES = ['Alta', 'Media', 'Baja'];

export const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    id_number: '',
    source: '',
    stage: 'Nuevo Prospecto',
    priority: 'Media',
    estimated_annual_premium: '',
    notes: '',
    next_action_date: ''
  });
  
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityType, setActivityType] = useState('note');
  const [activityDescription, setActivityDescription] = useState('');

  useEffect(() => {
    // Detectar si estamos en modo de creación o edición
    const isNewMode = leadId === 'new' || !leadId || window.location.pathname.endsWith('/new');
    
    if (isNewMode) {
      // Modo de creación de nuevo lead
      setLoading(false);
      setIsEditing(true);
    } else if (leadId) {
      // Modo de edición de lead existente
      loadLead();
    } else {
      // Fallback
      setLoading(false);
    }
  }, [leadId]);

  const loadLead = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/leads/${leadId}`);
      setLead(response.data);
      
      setFormData({
        name: response.data.name || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        id_number: response.data.id_number || '',
        source: response.data.source || '',
        stage: response.data.stage || 'Nuevo Prospecto',
        priority: response.data.priority || 'Media',
        estimated_annual_premium: response.data.estimated_annual_premium || '',
        notes: response.data.notes || '',
        next_action_date: response.data.next_action_date ?
          new Date(response.data.next_action_date).toISOString().slice(0, 16) : ''
      });
    } catch (error) {
      console.error('Error loading lead:', error);
      toast.error('Error al cargar lead');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!formData.name) {
        toast.error('El nombre es requerido');
        return;
      }
      
      const data = {
        ...formData,
        estimated_annual_premium: formData.estimated_annual_premium ? parseFloat(formData.estimated_annual_premium) : null,
        next_action_date: formData.next_action_date || null
      };
      
      // Detectar modo de creación
      const isNewMode = leadId === 'new' || !leadId || window.location.pathname.endsWith('/new');
      
      if (isNewMode) {
        // Crear nuevo lead
        await api.post('/leads', data);
        toast.success('Lead creado exitosamente');
        navigate('/leads');
      } else {
        // Actualizar lead existente
        await api.put(`/leads/${leadId}`, data);
        toast.success('Lead actualizado exitosamente');
        setIsEditing(false);
        loadLead();
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Error al guardar lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success('Lead eliminado');
      navigate('/pipeline');
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Error al eliminar lead');
    }
  };

  const handleAddActivity = async () => {
    try {
      if (!activityDescription) {
        toast.error('Descripción requerida');
        return;
      }
      
      await api.post(`/leads/${leadId}/activities`, {
        type: activityType,
        description: activityDescription
      });
      
      toast.success('Actividad agregada');
      setActivityModalOpen(false);
      setActivityDescription('');
      loadLead();
    } catch (error) {
      console.error('Error adding activity:', error);
      toast.error('Error al agregar actividad');
    }
  };

  const handleConvert = async () => {
    try {
      const response = await api.post(`/leads/${leadId}/convert`, {
        create_client_notes: formData.notes
      });
      
      toast.success('Lead convertido a cliente exitosamente');
      setConvertModalOpen(false);
      
      // Redirigir al perfil del cliente
      navigate(`/clients/${response.data.client_id}`);
    } catch (error) {
      console.error('Error converting lead:', error);
      toast.error(error.response?.data?.detail || 'Error al convertir lead');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando lead...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/pipeline')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al Pipeline
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {leadId === 'new' ? 'Nuevo Lead' : (lead?.name || 'Lead')}
            </h1>
            <p className="text-slate-600">
              {leadId === 'new' ? 'Crear un nuevo prospecto' : 'Detalle del prospecto'}
            </p>
          </div>
          
          {leadId !== 'new' && (
            <div className="flex gap-2">
              {!lead?.is_converted && (
                <>
                  {!isEditing ? (
                    <>
                      <Button variant="outline" onClick={() => setIsEditing(true)}>
                        Editar
                      </Button>
                      <Button onClick={() => setConvertModalOpen(true)}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Convertir a Cliente
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => {
                        const isNewMode = leadId === 'new' || !leadId || window.location.pathname.endsWith('/new');
                        if (isNewMode) {
                          navigate('/leads');
                        } else {
                          setIsEditing(false);
                          loadLead();
                        }
                      }}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSave} disabled={saving}>
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Guardar
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </>
              )}
              
              {!isEditing && !lead?.is_converted && (
                <Button variant="destructive" onClick={handleDelete}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          
          {leadId === 'new' && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Crear Lead'}
            </Button>
          )}
        </div>
      </div>

      {/* Converted Alert */}
      {lead?.is_converted && (
        <Alert className="mb-6 bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-900">
            <strong>Lead convertido a cliente</strong> el {formatDate(lead.converted_at)}.
            <Button
              variant="link"
              className="ml-2"
              onClick={() => navigate(`/clients/${lead.converted_to_client_id}`)}
            >
              Ver perfil del cliente
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información del Lead */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Información del Lead</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre Completo *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    disabled={!isEditing && leadId !== 'new'}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>RFC/CURP</Label>
                  <Input
                    value={formData.id_number}
                    onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                    disabled={!isEditing && leadId !== 'new'}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    disabled={!isEditing && leadId !== 'new'}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    disabled={!isEditing && leadId !== 'new'}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Fuente</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => setFormData({...formData, source: value})}
                    disabled={!isEditing && leadId !== 'new'}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecciona fuente" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Prioridad</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({...formData, priority: value})}
                    disabled={!isEditing && leadId !== 'new'}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {priority}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Prima Anual Estimada</Label>
                  <Input
                    type="number"
                    value={formData.estimated_annual_premium}
                    onChange={(e) => setFormData({...formData, estimated_annual_premium: e.target.value})}
                    disabled={!isEditing && leadId !== 'new'}
                    className="mt-1"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <Label>Fecha de Próxima Acción</Label>
                  <Input
                    type="datetime-local"
                    value={formData.next_action_date}
                    onChange={(e) => setFormData({...formData, next_action_date: e.target.value})}
                    disabled={!isEditing && leadId !== 'new'}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  disabled={!isEditing && leadId !== 'new'}
                  rows={4}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline de Actividades */}
        {leadId !== 'new' && lead && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Timeline</CardTitle>
                  {!lead.is_converted && (
                    <Button
                      size="sm"
                      onClick={() => setActivityModalOpen(true)}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Agregar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {lead.activities?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No hay actividades
                    </p>
                  ) : (
                    lead.activities?.map((activity) => {
                      // Formatear descripción para cambios de etapa
                      const formattedDescription = activity.type === 'stage_change' 
                        ? formatStageChangeDescription(activity.description)
                        : activity.description;
                      
                      return (
                        <div key={activity.id} className="border-l-2 border-blue-200 pl-4 pb-4 last:pb-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  {ACTIVITY_TYPE_LABELS[activity.type] || activity.type}
                                </Badge>
                                <span className="text-xs text-slate-500">
                                  {formatDate(activity.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-slate-900 leading-relaxed">
                                {formattedDescription}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                Por: {activity.created_by}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Información</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-600">Etapa actual:</span>
                  <Badge className="ml-2">{lead.stage}</Badge>
                </div>
                <div>
                  <span className="text-slate-600">Creado:</span>
                  <span className="ml-2">{formatDate(lead.created_at)}</span>
                </div>
                <div>
                  <span className="text-slate-600">Actualizado:</span>
                  <span className="ml-2">{formatDate(lead.updated_at)}</span>
                </div>
                {lead.estimated_annual_premium && (
                  <div>
                    <span className="text-slate-600">Valor estimado:</span>
                    <span className="ml-2 font-semibold text-green-600">
                      {formatCurrency(lead.estimated_annual_premium)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Convert Modal */}
      <Dialog open={convertModalOpen} onOpenChange={setConvertModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convertir Lead a Cliente</DialogTitle>
            <DialogDescription>
              Se creará un nuevo cliente con la información del lead.
              Podrás vincular pólizas después de la conversión.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Cliente: <strong>{lead?.name}</strong>
                {lead?.phone && <><br />Teléfono: {lead.phone}</>}
                {lead?.email && <><br />Email: {lead.email}</>}
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConvert}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Convertir a Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Modal */}
      <Dialog open={activityModalOpen} onOpenChange={setActivityModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Actividad</DialogTitle>
            <DialogDescription>
              Registra una nota, llamada o reunión
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Tipo de Actividad</Label>
              <Select value={activityType} onValueChange={setActivityType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Nota</SelectItem>
                  <SelectItem value="call">Llamada</SelectItem>
                  <SelectItem value="meeting">Reunión</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={activityDescription}
                onChange={(e) => setActivityDescription(e.target.value)}
                rows={4}
                className="mt-1"
                placeholder="Describe la actividad..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddActivity}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetail;